import {
  TIMELINE_FILTERS,
  getDefaultTimelineFilters,
  isMasterTechnicalLogMessage,
  isRedundantVideoCallEndedLog,
  isRedundantXproVideoRecordingAttachment,
  isVideoRecordingReadyLogMessage,
  isVoiceCallTranscriptMessage,
  matchesTimelineFilters,
  sanitizeTimelineFilters,
} from "../../../components/MessageHistory/messageTypeRegistry";
import { getConversationMessagesById } from "../../../api/client";
import { enrichMessageLocationFromApi } from "../../../utils/enrichMessageLocation";
import {
  readWorkspaceLocalJson,
  WS_KEY,
} from "../../../utils/workspaceStorage";

const PRINT_PAGE_SIZE = 100;
const PRINT_MAX_MESSAGES = 5000;

function extractMessageList(payload) {
  if (Array.isArray(payload?.messages)) return payload.messages;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function oldestNumericId(list) {
  let oldest = null;
  for (const msg of list) {
    const numeric = Number(msg?.id);
    if (!Number.isFinite(numeric)) continue;
    if (oldest == null || numeric < oldest) oldest = numeric;
  }
  return oldest;
}

const TIMELINE_FILTER_I18N = {
  [TIMELINE_FILTERS.CONVERSATION]: "timeline.filterConversation",
  [TIMELINE_FILTERS.INTERNAL]: "timeline.filterInternal",
  [TIMELINE_FILTERS.ACTIVITY]: "timeline.filterActivity",
  [TIMELINE_FILTERS.ACCESS]: "timeline.filterAccess",
  [TIMELINE_FILTERS.MEDIA]: "timeline.filterMedia",
  [TIMELINE_FILTERS.EMAIL]: "timeline.filterEmail",
  [TIMELINE_FILTERS.BOT]: "timeline.filterBot",
  [TIMELINE_FILTERS.SURVEY]: "timeline.filterSurvey",
  [TIMELINE_FILTERS.API_LOGS]: "timeline.filterApiLogs",
  [TIMELINE_FILTERS.AI_LOGS]: "timeline.filterAiLogs",
};

export function describeTimelineFiltersForPrint(filters, translate) {
  const list = Array.isArray(filters) ? filters.filter(Boolean) : [];
  if (typeof translate !== "function") return "";
  if (!list.length) return translate("pane.print.filtersAll");
  const names = list
    .map((id) => translate(TIMELINE_FILTER_I18N[id] || id))
    .filter((name) => name && String(name).trim());
  return translate("pane.print.filtersApplied", { filters: names.join(", ") });
}

/**
 * Filtros da timeline no mesmo ponto em que o histórico os persiste.
 */
export function readTimelineFiltersForPrint(canViewApiAudit = false) {
  const stored = readWorkspaceLocalJson(WS_KEY.CHAT_TIMELINE_FILTERS, null);
  if (Array.isArray(stored)) {
    return sanitizeTimelineFilters(stored, { canViewApiAudit });
  }
  return getDefaultTimelineFilters({ canViewApiAudit });
}

/**
 * Master vê request/response completos quando os logs técnicos estão na impressão:
 * filtro explícito ou "ver tudo" (lista vazia).
 */
export function shouldIncludeTechnicalPayloads(activeFilters, canViewApiAudit = false) {
  if (!canViewApiAudit) return { api: false, ai: false };
  const filters = Array.isArray(activeFilters) ? activeFilters.filter(Boolean) : [];
  if (!filters.length) return { api: true, ai: true };
  return {
    api: filters.includes(TIMELINE_FILTERS.API_LOGS),
    ai: filters.includes(TIMELINE_FILTERS.AI_LOGS),
  };
}

/** Mesma visibilidade da timeline do chat. */
export function selectMessagesForPrint(messages, { activeFilters = [], canViewApiAudit = false } = {}) {
  const list = Array.isArray(messages) ? messages : [];
  const withoutDupMedia = list.filter(
    (message) =>
      !isVoiceCallTranscriptMessage(message) &&
      !isVideoRecordingReadyLogMessage(message) &&
      !isRedundantVideoCallEndedLog(message, list) &&
      !isRedundantXproVideoRecordingAttachment(message, list)
  );
  const visible = canViewApiAudit
    ? withoutDupMedia
    : withoutDupMedia.filter((message) => !isMasterTechnicalLogMessage(message));
  return visible.filter((message) => matchesTimelineFilters(message, activeFilters));
}

/**
 * Percorre a paginação da solicitação até o fim (ou o teto de segurança).
 */
export async function loadConversationMessagesForPrint(conversationId) {
  const collected = [];
  const seen = new Set();
  let beforeMessageId = null;

  while (collected.length < PRINT_MAX_MESSAGES) {
    const payload = await getConversationMessagesById(conversationId, {
      limit: PRINT_PAGE_SIZE,
      beforeMessageId: beforeMessageId ?? undefined,
      returnRaw: true,
    });
    const list = extractMessageList(payload);
    if (!list.length) {
      return { messages: collected, truncated: false };
    }

    for (const msg of list) {
      if (msg?.id == null) continue;
      const key = String(msg.id);
      if (seen.has(key)) continue;
      seen.add(key);
      collected.push(enrichMessageLocationFromApi(msg));
      if (collected.length >= PRINT_MAX_MESSAGES) break;
    }

    const hasMore = Boolean(payload?.pagination?.hasMore);
    if (!hasMore) {
      return { messages: collected, truncated: false };
    }

    const oldest = oldestNumericId(list);
    if (oldest == null || oldest === beforeMessageId) {
      return { messages: collected, truncated: true };
    }
    beforeMessageId = oldest;

    if (collected.length >= PRINT_MAX_MESSAGES) {
      return { messages: collected, truncated: true };
    }
  }

  return { messages: collected, truncated: true };
}
